import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import codingAgentsCopy, { type CodingAgentsCopy } from '@/i18n/ns/coding-agents';
import type { ConsentState, ScopeAvailability } from './consent-model';
import type { CodingAgentGrant, ScopeDescriptor } from './coding-agents-types';

// Plain words for Connections → Coding agents, read from the `coding-agents`
// namespace (English and Swedish). Scope labels and descriptions come from the
// server; this file only frames them.

export function buildCodingAgentsCopy(ns: CodingAgentsCopy) {
  const consentHeading: Record<ConsentState, string> = {
    requested: ns.headingAsking,
    partly_beyond_rights: ns.headingAsking,
    beyond_rights: ns.headingAsking,
    refused: ns.headingRefused,
    consented: ns.headingConsented,
    revoked: ns.headingRevoked,
    stale: ns.headingStale,
  };
  const availabilityLabel: Record<ScopeAvailability, string> = {
    asked: ns.availAsked,
    already: ns.availAlready,
    not_available: ns.availNotAvailable,
  };
  const consentNote: Partial<Record<ConsentState, string>> = {
    partly_beyond_rights: ns.notePartly,
    beyond_rights: ns.noteBeyond,
    stale: ns.noteStale,
    revoked: ns.noteRevoked,
    consented: ns.noteConsented,
  };
  const grantStateLabel: Record<CodingAgentGrant['state'], string> = {
    active: ns.stateActive,
    revoked: ns.stateRevoked,
    expired: ns.stateExpired,
  };
  return {
    CODING_AGENTS_TITLE: ns.title,
    CODING_AGENTS_INTRO: ns.intro,
    CODING_AGENTS_NONE: ns.none,
    CODING_AGENTS_LOADING: ns.loading,
    CODING_AGENTS_ERROR: ns.error,
    consentTitle: (company: string) => formatCopy(ns.consentTitle, { company }),
    notVerified: (client: string, company: string, origin?: string | null) =>
      origin
        ? formatCopy(ns.notVerifiedOrigin, { client, company, origin })
        : formatCopy(ns.notVerified, { client, company }),
    dataLine: (vendor: string, person: string) => formatCopy(ns.dataLine, { vendor, person }),
    expiryLine: (days: number) =>
      formatCopy(days === 1 ? ns.expiryDay : ns.expiryDays, { days: String(days) }),
    consentHeading,
    availabilityLabel,
    NOT_AVAILABLE_NOTE: (company: string) => formatCopy(ns.notAvailableNote, { company }),
    consentNote,
    // Refusal reasons the server reports; anything else gets the general line.
    refusalText: (reason: string | null): string =>
      reason === 'knowledge_read_required' ? ns.refusalReadRequired : ns.refusalGeneral,
    CONSENT_ALLOW: ns.allow,
    CONSENT_DENY: ns.deny,
    CONSENT_WORKING: ns.working,
    // The answer never came back, so whether the choice was recorded is not known.
    CONSENT_UNCONFIRMED: ns.unconfirmed,
    CONSENT_CHECK: ns.check,
    CONSENT_STILL_PENDING: ns.stillPending,
    CONSENT_STILL_UNKNOWN: ns.stillUnknown,
    CONSENT_CLOSED: ns.requestClosed,
    consentRecordedOther: (settled: 'allow' | 'deny'): string =>
      formatCopy(ns.recordedOther, {
        choice: settled === 'allow' ? ns.choiceAllow : ns.choiceDeny,
      }),
    CONSENT_STALE_ON_SEND: ns.staleOnSend,
    CONSENT_REFUSED_ON_SEND: ns.refusedOnSend,
    scopeSummary: (scopes: ScopeDescriptor[]): string =>
      scopes.length === 0 ? ns.noAccess : scopes.map((scope) => scope.label).join(' · '),
    lastSeenLine: (grant: CodingAgentGrant, formatted: string): string =>
      grant.lastSeenAt === null || grant.seenState === 'never'
        ? ns.lastSeenNever
        : grant.seenState === 'stale'
          ? formatCopy(ns.lastSeenStale, { when: formatted })
          : formatCopy(ns.lastSeen, { when: formatted }),
    grantStateLabel,
    NEEDS_CONSENT: ns.needsConsent,
    REVIEW_REQUEST: ns.review,
    REVOKE: ns.revoke,
    revokeConfirm: (client: string) => formatCopy(ns.revokeConfirm, { client }),
    REVOKE_CONFIRM_YES: ns.revokeYes,
    REVOKE_CONFIRM_NO: ns.revokeNo,
    REVOKE_FAILED: ns.revokeFailed,
    REVOKE_DENIED: ns.revokeDenied,
    REVOKE_GONE: ns.revokeGone,
    YOUR_AGENTS: ns.yourAgents,
    COMPANY_AGENTS: ns.companyAgents,
    COMPANY_AGENTS_HELP: ns.companyAgentsHelp,
  };
}

export type CodingAgentsText = ReturnType<typeof buildCodingAgentsCopy>;

/** The copy in the app's interface language (English fallback). */
export const useCodingAgentsCopy = (): CodingAgentsText =>
  buildCodingAgentsCopy(useNamespaceCopy(codingAgentsCopy));
